import React, { useState, useEffect } from 'react';
import { COLORS } from '../utils/constants';
import { useLanguage } from '../context/LanguageContext';
import { idoAPI, userAPI } from '../services/api';
import MobileIDOPage from '../components/mobile/MobileIDOPage';

import { 
  Rocket, Clock, Star, TrendingUp, Users, ChevronRight,
  ArrowRight, Check, AlertCircle, Wallet, ShoppingCart,
  ArrowUpRight, ArrowDownRight, History, RefreshCw, Package
} from 'lucide-react';

const IDOPage = () => {
  const { t } = useLanguage();
  const [projects, setProjects] = useState([]);
  const [holdings, setHoldings] = useState([]);
  const [history, setHistory] = useState([]);
  const [userBalance, setUserBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [buyModal, setBuyModal] = useState(null);
  const [sellModal, setSellModal] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showAllHistory, setShowAllHistory] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!isMobile) {
      loadData();
    }
  }, [isMobile]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [projectsRes, holdingsRes, historyRes, balanceRes] = await Promise.all([
        idoAPI.getProjects(),
        idoAPI.getMyHoldings(),
        idoAPI.getMyHistory(),
        userAPI.getBalance(),
      ]);
      
      setProjects(projectsRes.data.projects || []);
      setHoldings(holdingsRes.data.holdings || []);
      setHistory(historyRes.data.transactions || []);
      setUserBalance(balanceRes.data.exchange_balance || 0);
    } catch (err) {
      console.error('Failed to load IDO data:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' }) + 
           ' ' + date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  };

  const formatNumber = (num, decimals = 2) => {
    if (num === null || num === undefined) return '-';
    return num.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  };

  // Categorize projects
  const liveProjects = projects.filter(p => p.is_launched && p.buy_enabled);
  const upcomingProjects = projects.filter(p => !p.is_launched);
  const closedProjects = projects.filter(p => p.is_launched && !p.buy_enabled && !p.sell_enabled);
  
  // Sellable projects - only show if user has holdings to sell
  const sellableProjects = projects.filter(p => {
    if (!p.is_launched || !p.sell_enabled || !p.sell_price) return false;
    const holding = holdings.find(h => h.asset === p.symbol);
    return holding && holding.quantity > 0;
  });

  // Calculate portfolio value
  const portfolioValue = holdings.reduce((sum, h) => sum + (h.current_value || 0), 0);

  // Render mobile version
  if (isMobile) {
    return <MobileIDOPage />;
  }

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '300px',
        color: COLORS.textSecondary 
      }}>
        <RefreshCw size={24} className="animate-spin" />
      </div>
    );
  }

  return (
    <div style={{ paddingBottom: '20px' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: isMobile ? '16px' : '20px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Rocket size={isMobile ? 20 : 24} color={COLORS.primary} />
          <h1 style={{ 
            fontSize: isMobile ? '18px' : '24px', 
            fontWeight: '700', 
            color: COLORS.text, 
            margin: 0 
          }}>
            IDO
          </h1>
        </div>
        <button
          onClick={loadData}
          style={{
            padding: '8px',
            backgroundColor: COLORS.card,
            border: `1px solid ${COLORS.border}`,
            borderRadius: '8px',
            color: COLORS.textSecondary,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <RefreshCw size={16} />
        </button>
      </div>

      {/* Balance Bar */}
      <div style={{
        backgroundColor: COLORS.card,
        border: `1px solid ${COLORS.border}`,
        borderRadius: '12px',
        padding: isMobile ? '12px' : '14px 16px',
        marginBottom: isMobile ? '16px' : '20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <div>
          <div style={{ color: COLORS.textSecondary, fontSize: '11px', marginBottom: '2px' }}>
            {t('balance')} USDT
          </div>
          <div style={{ color: COLORS.text, fontSize: isMobile ? '18px' : '20px', fontWeight: '700' }}>
            ${formatNumber(userBalance)}
          </div>
        </div>
        {holdings.length > 0 && (
          <div style={{ textAlign: 'right' }}>
            <div style={{ color: COLORS.textSecondary, fontSize: '11px', marginBottom: '2px' }}>
              {t('idoPortfolio')}
            </div>
            <div style={{ color: COLORS.primary, fontSize: isMobile ? '18px' : '20px', fontWeight: '700' }}>
              ${formatNumber(portfolioValue)}
            </div>
          </div>
        )}
      </div>

      {/* Live IDO Section */}
      {liveProjects.length > 0 && (
        <Section title={`🟢 ${t('openForBuy')}`} count={liveProjects.length} isMobile={isMobile}>
          {liveProjects.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              holding={holdings.find(h => h.asset === project.symbol)}
              onBuy={() => setBuyModal(project)}
              onSell={(holding) => setSellModal({ project, holding })}
              onDetails={() => setSelectedProject(project)}
              isMobile={isMobile}
              t={t}
            />
          ))}
        </Section>
      )}

      {/* Sellable IDO Section */}
      {sellableProjects.length > 0 && (
        <Section title={`🔴 ${t('openForSell')}`} count={sellableProjects.length} isMobile={isMobile}>
          {sellableProjects.map((project) => {
            const holding = holdings.find(h => h.asset === project.symbol);
            if (!holding) return null;
            return (
              <SellableCard
                key={project.id}
                project={project}
                holding={holding}
                onSell={() => setSellModal({ project, holding })}
                onDetails={() => setSelectedProject(project)}
                isMobile={isMobile}
                t={t}
              />
            );
          })}
        </Section>
      )}

      {/* Upcoming IDO Section */}
      {upcomingProjects.length > 0 && (
        <Section title="⏳ Скоро" count={upcomingProjects.length} isMobile={isMobile}>
          {upcomingProjects.map((project) => (
            <UpcomingCard key={project.id} project={project} isMobile={isMobile} t={t} />
          ))}
        </Section>
      )}

      {/* My Holdings Section */}
      {holdings.length > 0 && (
        <Section title={t("idoTokens")} count={holdings.length} isMobile={isMobile}>
          {holdings.map((holding, idx) => {
            const project = projects.find(p => p.symbol === holding.asset);
            return (
              <HoldingCard
                key={holding.id || idx}
                holding={holding}
                project={project}
                onSell={() => project && setSellModal({ project, holding })}
                isMobile={isMobile}
                t={t}
              />
            );
          })}
        </Section>
      )}

      {/* Closed IDO Section */}
      {closedProjects.length > 0 && (
        <Section title="🔒 Завершены" count={closedProjects.length} collapsed isMobile={isMobile}>
          {closedProjects.map((project) => (
            <ClosedCard key={project.id} project={project} isMobile={isMobile} />
          ))}
        </Section>
      )}

      {/* History Section */}
      {history.length > 0 && (
        <Section 
          title={t("depositHistory")} 
          count={history.length} 
          isMobile={isMobile}
          action={history.length > 3 && !showAllHistory ? (
            <button
              onClick={() => setShowAllHistory(true)}
              style={{
                background: 'none',
                border: 'none',
                color: COLORS.primary,
                fontSize: '12px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              Все <ChevronRight size={14} />
            </button>
          ) : null}
        >
          {(showAllHistory ? history : history.slice(0, 3)).map((tx, idx) => (
            <HistoryItem key={tx.id || idx} tx={tx} formatDate={formatDate} isMobile={isMobile} t={t} />
          ))}
        </Section>
      )}

      {/* Empty State */}
      {projects.length === 0 && holdings.length === 0 && (
        <div style={{
          backgroundColor: COLORS.card,
          border: `1px solid ${COLORS.border}`,
          borderRadius: '12px',
          padding: '40px 20px',
          textAlign: 'center',
        }}>
          <Rocket size={40} color={COLORS.textSecondary} style={{ marginBottom: '12px', opacity: 0.5 }} />
          <div style={{ color: COLORS.textSecondary, fontSize: '14px' }}>
            Нет доступных IDO проектов
          </div>
        </div>
      )}

      {/* Buy Modal */}
      {buyModal && (
        <BuyModal
          project={buyModal}
          userBalance={userBalance}
          onClose={() => setBuyModal(null)}
          onSuccess={() => {
            setBuyModal(null);
            loadData();
          }}
          isMobile={isMobile}
          t={t}
        />
      )}

      {/* Sell Modal */}
      {sellModal && (
        <SellModal
          project={sellModal.project}
          holding={sellModal.holding}
          onClose={() => setSellModal(null)}
          onSuccess={() => {
            setSellModal(null);
            loadData();
          }}
          isMobile={isMobile}
          t={t}
        />
      )}

      {/* Project Details Modal */}
      {selectedProject && (
        <ProjectDetailsModal
          project={selectedProject}
          holding={holdings.find(h => h.asset === selectedProject.symbol)}
          onClose={() => setSelectedProject(null)}
          onBuy={() => {
            setSelectedProject(null);
            setBuyModal(selectedProject);
          }}
          onSell={(holding) => {
            setSelectedProject(null);
            setSellModal({ project: selectedProject, holding });
          }}
          isMobile={isMobile}
          t={t}
        />
      )}
    </div>
  );
};

// ================== SECTION COMPONENT ==================
const Section = ({ title, count, children, collapsed: initialCollapsed, action, isMobile }) => {
  const [collapsed, setCollapsed] = useState(initialCollapsed || false);
  
  return (
    <div style={{ marginBottom: isMobile ? '20px' : '24px' }}>
      <div 
        style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: collapsed ? 0 : '10px',
          cursor: initialCollapsed ? 'pointer' : 'default',
        }}
        onClick={() => initialCollapsed && setCollapsed(!collapsed)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <h3 style={{ 
            color: COLORS.text, 
            fontSize: isMobile ? '14px' : '15px', 
            fontWeight: '600', 
            margin: 0 
          }}>
            {title}
          </h3>
          <span style={{
            backgroundColor: COLORS.background,
            color: COLORS.textSecondary,
            fontSize: '11px',
            padding: '2px 6px',
            borderRadius: '10px',
          }}>
            {count}
          </span>
        </div>
        {action || (initialCollapsed && (
          <ChevronRight 
            size={16} 
            color={COLORS.textSecondary}
            style={{ 
              transform: collapsed ? 'rotate(0deg)' : 'rotate(90deg)',
              transition: 'transform 0.2s',
            }}
          />
        ))}
      </div>
      {!collapsed && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {children}
        </div>
      )}
    </div>
  );
};

// ================== PROJECT CARD ==================
const ProjectCard = ({ project, holding, onBuy, onSell, onDetails, isMobile, t }) => {
  const progress = ((project.sold_amount || 0) / project.total_supply) * 100;
  const remaining = project.total_supply - (project.sold_amount || 0);

  return (
    <div style={{
      backgroundColor: COLORS.card,
      border: `1px solid ${COLORS.primary}50`,
      borderRadius: '12px',
      overflow: 'hidden',
    }}>
      {/* Main Info */}
      <div style={{ padding: isMobile ? '12px' : '14px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
          {/* Logo */}
          {/* Logo */}
          <div 
            onClick={onDetails}
            style={{
              width: isMobile ? '40px' : '48px',
              height: isMobile ? '40px' : '48px',
              borderRadius: '10px',
              backgroundColor: COLORS.background,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              flexShrink: 0,
              cursor: 'pointer',
            }}
          >
            {project.logo ? (
              <img src={project.logo} alt={project.symbol} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <span style={{ color: COLORS.primary, fontWeight: '700', fontSize: isMobile ? '14px' : '16px' }}>
                {project.symbol?.charAt(0)}
              </span>
            )}
          </div>
          
          {/* Info */}
          <div style={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={onDetails}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
              <span style={{ color: COLORS.text, fontSize: isMobile ? '14px' : '15px', fontWeight: '600' }}>
                {project.name}
              </span>
              <span style={{
                backgroundColor: COLORS.primary,
                color: '#000',
                fontSize: '10px',
                fontWeight: '700',
                padding: '2px 6px',
                borderRadius: '4px',
              }}>
                ${project.symbol}
              </span>
              {project.description && (
                <span style={{ color: COLORS.textSecondary, fontSize: '10px' }}>ℹ️</span>
              )}
            </div>
            
            <div style={{ display: 'flex', gap: isMobile ? '12px' : '16px', flexWrap: 'wrap' }}>
              <div>
                <div style={{ color: COLORS.textSecondary, fontSize: '10px' }}>{t('price')}</div>
                <div style={{ color: COLORS.success, fontSize: isMobile ? '13px' : '14px', fontWeight: '600' }}>
                  ${project.buy_price}
                </div>
              </div>
              <div>
                <div style={{ color: COLORS.textSecondary, fontSize: '10px' }}>{t('remaining')}</div>
                <div style={{ color: COLORS.text, fontSize: isMobile ? '13px' : '14px', fontWeight: '600' }}>
                  {remaining >= 1000000 ? `${(remaining / 1000000).toFixed(1)}M` : 
                   remaining >= 1000 ? `${(remaining / 1000).toFixed(0)}K` : remaining.toLocaleString()}
                </div>
              </div>
              <div>
                <div style={{ color: COLORS.textSecondary, fontSize: '10px' }}>{t('progress')}</div>
                <div style={{ color: COLORS.primary, fontSize: isMobile ? '13px' : '14px', fontWeight: '600' }}>
                  {progress.toFixed(1)}%
                </div>
              </div>
            </div>
          </div>

          {/* Buy Button */}
          <button
            onClick={onBuy}
            style={{
              padding: isMobile ? '8px 12px' : '10px 16px',
              backgroundColor: COLORS.primary,
              border: 'none',
              borderRadius: '8px',
              color: '#000',
              fontWeight: '600',
              fontSize: isMobile ? '12px' : '13px',
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            {t('buyButton')}
          </button>
        </div>

        {/* Progress Bar */}
        <div style={{
          height: '4px',
          backgroundColor: COLORS.background,
          borderRadius: '2px',
          marginTop: '10px',
          overflow: 'hidden',
        }}>
          <div style={{
            height: '100%',
            width: `${Math.min(progress, 100)}%`,
            backgroundColor: COLORS.primary,
            borderRadius: '2px',
          }} />
        </div>
      </div>

      {/* User Holdings (if any) */}
      {holding && (
        <div style={{
          padding: isMobile ? '10px 12px' : '10px 14px',
          backgroundColor: `${COLORS.success}08`,
          borderTop: `1px solid ${COLORS.border}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div>
            <span style={{ color: COLORS.textSecondary, fontSize: '11px' }}>{t('yourBalance')}: </span>
            <span style={{ color: COLORS.success, fontSize: '13px', fontWeight: '600' }}>
              {holding.quantity.toLocaleString()} {t('tokens')}
            </span>
            <span style={{ color: COLORS.textSecondary, fontSize: '11px' }}>
              {' '}(≈${(holding.quantity * (project.sell_price || project.buy_price)).toFixed(2)})
            </span>
          </div>
          {project.sell_enabled && project.sell_price && (
            <button
              onClick={() => onSell(holding)}
              style={{
                padding: '6px 12px',
                backgroundColor: `${COLORS.danger}15`,
                border: `1px solid ${COLORS.danger}50`,
                borderRadius: '6px',
                color: COLORS.danger,
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer',
              }}
            >
              {t('sellButton')}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// ================== UPCOMING CARD ==================
const UpcomingCard = ({ project, isMobile, t }) => {
  const launchDate = new Date(project.launch_date);
  const now = new Date();
  const diff = launchDate - now;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const timeText = days > 0 ? `${days}д ${hours}ч` : hours > 0 ? `${hours}ч` : 'Скоро';

  return (
    <div style={{
      backgroundColor: COLORS.card,
      border: `1px solid ${COLORS.warning}40`,
      borderRadius: '12px',
      padding: isMobile ? '12px' : '14px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
    }}>
      <div style={{
        width: isMobile ? '40px' : '48px',
        height: isMobile ? '40px' : '48px',
        borderRadius: '10px',
        backgroundColor: COLORS.background,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        flexShrink: 0,
      }}>
        {project.logo ? (
          <img src={project.logo} alt={project.symbol} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <span style={{ color: COLORS.warning, fontWeight: '700', fontSize: isMobile ? '14px' : '16px' }}>
            {project.symbol?.charAt(0)}
          </span>
        )}
      </div>
      
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
          <span style={{ color: COLORS.text, fontSize: isMobile ? '14px' : '15px', fontWeight: '600' }}>
            {project.name}
          </span>
          <span style={{ color: COLORS.textSecondary, fontSize: '12px' }}>
            ${project.symbol}
          </span>
        </div>
        <div style={{ color: COLORS.textSecondary, fontSize: '12px' }}>
          {t ? t('price') : 'Цена'}: ${project.buy_price} • Hard cap: {project.total_supply.toLocaleString()}
        </div>
      </div>
      
      <div style={{
        padding: '6px 10px',
        backgroundColor: `${COLORS.warning}15`,
        borderRadius: '6px',
        textAlign: 'center',
      }}>
        <div style={{ color: COLORS.warning, fontSize: isMobile ? '14px' : '16px', fontWeight: '700' }}>
          {timeText}
        </div>
      </div>
    </div>
  );
};

// ================== SELLABLE CARD ==================
const SellableCard = ({ project, holding, onSell, onDetails, isMobile, t }) => {
  const sellValue = holding.quantity * project.sell_price;

  return (
    <div 
      style={{
        backgroundColor: COLORS.card,
        border: `1px solid ${COLORS.danger}40`,
        borderRadius: '12px',
        padding: isMobile ? '12px' : '14px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        {/* Logo */}
        <div 
          onClick={onDetails}
          style={{
            width: isMobile ? '40px' : '48px',
            height: isMobile ? '40px' : '48px',
            borderRadius: '10px',
            backgroundColor: COLORS.background,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            flexShrink: 0,
            cursor: 'pointer',
          }}
        >
          {project.logo ? (
            <img src={project.logo} alt={project.symbol} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <span style={{ color: COLORS.danger, fontWeight: '700', fontSize: isMobile ? '14px' : '16px' }}>
              {project.symbol?.charAt(0)}
            </span>
          )}
        </div>
        
        {/* Info */}
        <div style={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={onDetails}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
            <span style={{ color: COLORS.text, fontSize: isMobile ? '14px' : '15px', fontWeight: '600' }}>
              {project.name}
            </span>
            <span style={{
              backgroundColor: COLORS.danger,
              color: '#fff',
              fontSize: '10px',
              fontWeight: '700',
              padding: '2px 6px',
              borderRadius: '4px',
            }}>
              SELL
            </span>
          </div>
          
          <div style={{ display: 'flex', gap: isMobile ? '10px' : '14px', flexWrap: 'wrap' }}>
            <div>
              <span style={{ color: COLORS.textSecondary, fontSize: '10px' }}>{t('balance')}: </span>
              <span style={{ color: COLORS.text, fontSize: '12px', fontWeight: '600' }}>
                {holding.quantity.toLocaleString()}
              </span>
            </div>
            <div>
              <span style={{ color: COLORS.textSecondary, fontSize: '10px' }}>{t('price')}: </span>
              <span style={{ color: COLORS.danger, fontSize: '12px', fontWeight: '600' }}>
                ${project.sell_price}
              </span>
            </div>
            <div>
              <span style={{ color: COLORS.textSecondary, fontSize: '10px' }}>{t('amount')}: </span>
              <span style={{ color: COLORS.success, fontSize: '12px', fontWeight: '600' }}>
                ${sellValue.toFixed(2)}
              </span>
            </div>
          </div>
        </div>

        {/* Sell Button */}
        <button
          onClick={onSell}
          style={{
            padding: isMobile ? '10px 14px' : '12px 18px',
            backgroundColor: COLORS.danger,
            border: 'none',
            borderRadius: '8px',
            color: '#fff',
            fontWeight: '600',
            fontSize: isMobile ? '12px' : '13px',
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          {t('sellButton')}
        </button>
      </div>
    </div>
  );
};

// ================== HOLDING CARD ==================
const HoldingCard = ({ holding, project, onSell, isMobile, t }) => {
  const canSell = project?.sell_enabled && project?.sell_price;
  
  return (
    <div style={{
      backgroundColor: COLORS.card,
      border: `1px solid ${COLORS.border}`,
      borderRadius: '12px',
      padding: isMobile ? '12px' : '14px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{
          width: isMobile ? '36px' : '40px',
          height: isMobile ? '36px' : '40px',
          borderRadius: '8px',
          backgroundColor: COLORS.background,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          flexShrink: 0,
        }}>
          {holding.logo ? (
            <img src={holding.logo} alt={holding.asset} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <span style={{ color: COLORS.primary, fontWeight: '700', fontSize: '14px' }}>
              {holding.asset?.charAt(0)}
            </span>
          )}
        </div>
        
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ color: COLORS.text, fontSize: isMobile ? '14px' : '15px', fontWeight: '600' }}>
              {holding.token_name || holding.asset}
            </span>
            <span style={{ color: COLORS.textSecondary, fontSize: '12px' }}>
              ${holding.asset}
            </span>
          </div>
          <div style={{ color: COLORS.textSecondary, fontSize: '11px', marginTop: '2px' }}>
            {t ? t('boughtAt') : 'Куплено по'} ${holding.avg_buy_price?.toFixed(6) || '0'}
          </div>
        </div>
        
        <div style={{ textAlign: 'right' }}>
          <div style={{ color: COLORS.text, fontSize: isMobile ? '14px' : '15px', fontWeight: '700' }}>
            {holding.quantity.toLocaleString()}
          </div>
          <div style={{ color: COLORS.success, fontSize: '12px' }}>
            ≈${(holding.current_value || 0).toFixed(2)}
          </div>
        </div>
        
        {canSell && (
          <button
            onClick={onSell}
            style={{
              padding: '8px 12px',
              backgroundColor: `${COLORS.danger}15`,
              border: `1px solid ${COLORS.danger}50`,
              borderRadius: '6px',
              color: COLORS.danger,
              fontSize: '12px',
              fontWeight: '600',
              cursor: 'pointer',
              marginLeft: '8px',
            }}
          >
            {t ? t('sellButton') : 'Sell'}
          </button>
        )}
      </div>
    </div>
  );
};

// ================== CLOSED CARD ==================
const ClosedCard = ({ project, isMobile }) => {
  const progress = ((project.sold_amount || 0) / project.total_supply) * 100;
  
  return (
    <div style={{
      backgroundColor: COLORS.card,
      border: `1px solid ${COLORS.border}`,
      borderRadius: '12px',
      padding: isMobile ? '10px 12px' : '12px 14px',
      opacity: 0.7,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{
          width: '32px',
          height: '32px',
          borderRadius: '8px',
          backgroundColor: COLORS.background,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}>
          {project.logo ? (
            <img src={project.logo} alt={project.symbol} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <span style={{ color: COLORS.textSecondary, fontWeight: '600', fontSize: '12px' }}>
              {project.symbol?.charAt(0)}
            </span>
          )}
        </div>
        
        <div style={{ flex: 1 }}>
          <span style={{ color: COLORS.text, fontSize: '13px', fontWeight: '500' }}>
            {project.name}
          </span>
          <span style={{ color: COLORS.textSecondary, fontSize: '12px', marginLeft: '6px' }}>
            ${project.symbol}
          </span>
        </div>
        
        <div style={{ color: COLORS.textSecondary, fontSize: '12px' }}>
          {progress.toFixed(0)}% продано
        </div>
      </div>
    </div>
  );
};

// ================== HISTORY ITEM ==================
const HistoryItem = ({ tx, formatDate, isMobile, t }) => {
  const isBuy = tx.type === 'buy';
  
  return (
    <div style={{
      backgroundColor: COLORS.card,
      border: `1px solid ${COLORS.border}`,
      borderRadius: '10px',
      padding: isMobile ? '10px 12px' : '12px 14px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
    }}>
      <div style={{
        width: '32px',
        height: '32px',
        borderRadius: '50%',
        backgroundColor: isBuy ? `${COLORS.success}15` : `${COLORS.danger}15`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}>
        {isBuy ? (
          <ArrowDownRight size={16} color={COLORS.success} />
        ) : (
          <ArrowUpRight size={16} color={COLORS.danger} />
        )}
      </div>
      
      <div style={{ flex: 1 }}>
        <div style={{ color: COLORS.text, fontSize: '13px', fontWeight: '500' }}>
          {isBuy ? (t ? t('purchase') : 'Покупка') : (t ? t('sale') : 'Продажа')} ${tx.ido_symbol}
        </div>
        <div style={{ color: COLORS.textSecondary, fontSize: '11px' }}>
          {formatDate(tx.created_at)}
        </div>
      </div>
      
      <div style={{ textAlign: 'right' }}>
        <div style={{ 
          color: isBuy ? COLORS.success : COLORS.danger, 
          fontSize: '13px', 
          fontWeight: '600' 
        }}>
          {isBuy ? '+' : '-'}{tx.token_amount?.toLocaleString()} {t ? t('tokens') : 'tokens'}
        </div>
        <div style={{ color: COLORS.textSecondary, fontSize: '11px' }}>
          {isBuy ? '-' : '+'}${tx.usdt_amount?.toFixed(2)}
        </div>
      </div>
    </div>
  );
};

// ================== BUY MODAL ==================
const BuyModal = ({ project, userBalance, onClose, onSuccess, isMobile, t }) => {
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const usdtAmount = parseFloat(amount) || 0;
  const tokenAmount = usdtAmount / project.buy_price;
  const remaining = project.total_supply - (project.sold_amount || 0);
  const maxPurchase = Math.min(
    userBalance,
    project.max_purchase || Infinity,
    remaining * project.buy_price
  );

  const handleBuy = async () => {
    if (usdtAmount <= 0) {
      setError('Введите сумму');
      return;
    }
    if (usdtAmount > userBalance) {
      setError('Недостаточно средств');
      return;
    }
    if (project.min_purchase && usdtAmount < project.min_purchase) {
      setError(`Минимум: $${project.min_purchase}`);
      return;
    }
    if (project.max_purchase && usdtAmount > project.max_purchase) {
      setError(`Максимум: $${project.max_purchase}`);
      return;
    }

    setLoading(true);
    setError('');

    try {
      await idoAPI.buyTokens(project.id, usdtAmount);
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка покупки');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalWrapper onClose={onClose}>
      <div style={{
        backgroundColor: COLORS.card,
        borderRadius: isMobile ? '16px 16px 0 0' : '16px',
        width: '100%',
        maxWidth: isMobile ? '100%' : '400px',
        maxHeight: '90vh',
        overflow: 'auto',
      }}>
        {/* Header */}
        <div style={{
          padding: '16px',
          borderBottom: `1px solid ${COLORS.border}`,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            backgroundColor: COLORS.background,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}>
            {project.logo ? (
              <img src={project.logo} alt={project.symbol} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <span style={{ color: COLORS.primary, fontWeight: '700', fontSize: '14px' }}>
                {project.symbol?.charAt(0)}
              </span>
            )}
          </div>
          <div>
            <h3 style={{ color: COLORS.text, fontSize: '16px', fontWeight: '700', margin: 0 }}>
              Купить ${project.symbol}
            </h3>
            <div style={{ color: COLORS.textSecondary, fontSize: '12px' }}>
              ${project.buy_price} за токен
            </div>
          </div>
        </div>

        {/* Content */}
        <div style={{ padding: '16px' }}>
          {error && (
            <div style={{
              padding: '10px',
              backgroundColor: `${COLORS.danger}15`,
              borderRadius: '8px',
              color: COLORS.danger,
              fontSize: '13px',
              marginBottom: '12px',
            }}>
              {error}
            </div>
          )}

          <div style={{ marginBottom: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
              <label style={{ color: COLORS.textSecondary, fontSize: '12px' }}>{t('amount')} USDT</label>
              <span style={{ color: COLORS.textSecondary, fontSize: '11px' }}>
                {t('balance')}: ${userBalance.toFixed(2)}
              </span>
            </div>
            <div style={{ position: 'relative' }}>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0"
                style={{
                  width: '100%',
                  padding: '14px',
                  paddingRight: '60px',
                  backgroundColor: COLORS.background,
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: '10px',
                  color: COLORS.text,
                  fontSize: '18px',
                  fontWeight: '600',
                  boxSizing: 'border-box',
                }}
              />
              <button
                onClick={() => setAmount(maxPurchase.toFixed(2))}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  padding: '6px 10px',
                  backgroundColor: COLORS.primary,
                  border: 'none',
                  borderRadius: '6px',
                  color: '#000',
                  fontSize: '11px',
                  fontWeight: '700',
                  cursor: 'pointer',
                }}
              >
                MAX
              </button>
            </div>
          </div>

          {/* Limits */}
          {(project.min_purchase || project.max_purchase) && (
            <div style={{ 
              display: 'flex', 
              gap: '16px', 
              marginBottom: '12px',
              fontSize: '11px',
              color: COLORS.textSecondary,
            }}>
              {project.min_purchase && <span>Мин: ${project.min_purchase}</span>}
              {project.max_purchase && <span>Макс: ${project.max_purchase}</span>}
            </div>
          )}

          {/* Result */}
          <div style={{
            padding: '14px',
            backgroundColor: COLORS.background,
            borderRadius: '10px',
            marginBottom: '16px',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
              <span style={{ color: COLORS.textSecondary, fontSize: '12px' }}>Вы получите</span>
              <span style={{ color: COLORS.primary, fontSize: '16px', fontWeight: '700' }}>
                {tokenAmount.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${project.symbol}
              </span>
            </div>
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={onClose}
              style={{
                flex: 1,
                padding: '14px',
                backgroundColor: COLORS.background,
                border: `1px solid ${COLORS.border}`,
                borderRadius: '10px',
                color: COLORS.textSecondary,
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
              }}
            >
              Отмена
            </button>
            <button
              onClick={handleBuy}
              disabled={loading || usdtAmount <= 0}
              style={{
                flex: 2,
                padding: '14px',
                backgroundColor: loading ? COLORS.background : COLORS.primary,
                border: 'none',
                borderRadius: '10px',
                color: loading ? COLORS.textSecondary : '#000',
                fontSize: '14px',
                fontWeight: '700',
                cursor: loading ? 'not-allowed' : 'pointer',
              }}
            >
              {loading ? t('loading') : t('buyButton')}
            </button>
          </div>
        </div>
      </div>
    </ModalWrapper>
  );
};

// ================== SELL MODAL ==================
const SellModal = ({ project, holding, onClose, onSuccess, isMobile, t }) => {
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const tokenAmount = parseFloat(amount) || 0;
  const usdtAmount = tokenAmount * project.sell_price;
  const maxSell = holding?.quantity || 0;

  const handleSell = async () => {
    if (tokenAmount <= 0) {
      setError('Введите количество');
      return;
    }
    if (tokenAmount > maxSell) {
      setError(t('insufficientTokens'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      await idoAPI.sellTokens(project.id, tokenAmount);
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка продажи');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalWrapper onClose={onClose}>
      <div style={{
        backgroundColor: COLORS.card,
        borderRadius: isMobile ? '16px 16px 0 0' : '16px',
        width: '100%',
        maxWidth: isMobile ? '100%' : '400px',
        maxHeight: '90vh',
        overflow: 'auto',
      }}>
        {/* Header */}
        <div style={{
          padding: '16px',
          borderBottom: `1px solid ${COLORS.border}`,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            backgroundColor: COLORS.background,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}>
            {project.logo ? (
              <img src={project.logo} alt={project.symbol} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <span style={{ color: COLORS.primary, fontWeight: '700', fontSize: '14px' }}>
                {project.symbol?.charAt(0)}
              </span>
            )}
          </div>
          <div>
            <h3 style={{ color: COLORS.text, fontSize: '16px', fontWeight: '700', margin: 0 }}>
              Продать ${project.symbol}
            </h3>
            <div style={{ color: COLORS.textSecondary, fontSize: '12px' }}>
              ${project.sell_price} за токен
            </div>
          </div>
        </div>

        {/* Content */}
        <div style={{ padding: '16px' }}>
          {error && (
            <div style={{
              padding: '10px',
              backgroundColor: `${COLORS.danger}15`,
              borderRadius: '8px',
              color: COLORS.danger,
              fontSize: '13px',
              marginBottom: '12px',
            }}>
              {error}
            </div>
          )}

          <div style={{ marginBottom: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
              <label style={{ color: COLORS.textSecondary, fontSize: '12px' }}>{t('quantity')}</label>
              <span style={{ color: COLORS.textSecondary, fontSize: '11px' }}>
                {t('balance')}: {maxSell.toLocaleString()}
              </span>
            </div>
            <div style={{ position: 'relative' }}>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0"
                style={{
                  width: '100%',
                  padding: '14px',
                  paddingRight: '60px',
                  backgroundColor: COLORS.background,
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: '10px',
                  color: COLORS.text,
                  fontSize: '18px',
                  fontWeight: '600',
                  boxSizing: 'border-box',
                }}
              />
              <button
                onClick={() => setAmount(maxSell.toString())}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  padding: '6px 10px',
                  backgroundColor: COLORS.danger,
                  border: 'none',
                  borderRadius: '6px',
                  color: '#fff',
                  fontSize: '11px',
                  fontWeight: '700',
                  cursor: 'pointer',
                }}
              >
                ВСЁ
              </button>
            </div>
          </div>

          {/* Result */}
          <div style={{
            padding: '14px',
            backgroundColor: COLORS.background,
            borderRadius: '10px',
            marginBottom: '16px',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: COLORS.textSecondary, fontSize: '12px' }}>Вы получите</span>
              <span style={{ color: COLORS.success, fontSize: '16px', fontWeight: '700' }}>
                ${usdtAmount.toFixed(2)} USDT
              </span>
            </div>
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={onClose}
              style={{
                flex: 1,
                padding: '14px',
                backgroundColor: COLORS.background,
                border: `1px solid ${COLORS.border}`,
                borderRadius: '10px',
                color: COLORS.textSecondary,
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
              }}
            >
              Отмена
            </button>
            <button
              onClick={handleSell}
              disabled={loading || tokenAmount <= 0}
              style={{
                flex: 2,
                padding: '14px',
                backgroundColor: loading ? COLORS.background : COLORS.danger,
                border: 'none',
                borderRadius: '10px',
                color: loading ? COLORS.textSecondary : '#fff',
                fontSize: '14px',
                fontWeight: '700',
                cursor: loading ? 'not-allowed' : 'pointer',
              }}
            >
              {loading ? t('loading') : t('sellButton')}
            </button>
          </div>
        </div>
      </div>
    </ModalWrapper>
  );
};

// ================== PROJECT DETAILS MODAL ==================
const ProjectDetailsModal = ({ project, holding, onClose, onBuy, onSell, isMobile, t }) => {
  const progress = ((project.sold_amount || 0) / project.total_supply) * 100;
  const remaining = project.total_supply - (project.sold_amount || 0);

  return (
    <ModalWrapper onClose={onClose}>
      <div style={{
        backgroundColor: COLORS.card,
        borderRadius: isMobile ? '16px 16px 0 0' : '16px',
        width: '100%',
        maxWidth: isMobile ? '100%' : '440px',
        maxHeight: '90vh',
        overflow: 'auto',
      }}>
        {/* Header */}
        <div style={{
          padding: '16px',
          borderBottom: `1px solid ${COLORS.border}`,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            backgroundColor: COLORS.background,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}>
            {project.logo ? (
              <img src={project.logo} alt={project.symbol} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <span style={{ color: COLORS.primary, fontWeight: '800', fontSize: '18px' }}>
                {project.symbol?.charAt(0)}
              </span>
            )}
          </div>
          <div style={{ flex: 1 }}>
            <h3 style={{ color: COLORS.text, fontSize: '18px', fontWeight: '700', margin: 0 }}>
              {project.name}
            </h3>
            <div style={{ color: COLORS.textSecondary, fontSize: '13px' }}>
              ${project.symbol}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: COLORS.textSecondary,
              cursor: 'pointer',
              padding: '8px',
            }}
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '16px' }}>
          {project.description && (
            <p style={{ color: COLORS.textSecondary, fontSize: '13px', marginBottom: '16px' }}>
              {project.description}
            </p>
          )}

          {/* Stats Grid */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(2, 1fr)', 
            gap: '10px',
            marginBottom: '16px',
          }}>
            <StatItem label={t('buyPrice')} value={`$${project.buy_price}`} color={COLORS.success} />
            <StatItem label={t('sellPrice')} value={project.sell_price ? `$${project.sell_price}` : 'N/A'} color={project.sell_enabled ? COLORS.danger : COLORS.textSecondary} />
            <StatItem label={t('remaining')} value={remaining.toLocaleString()} />
            <StatItem label={t('progress')} value={`${progress.toFixed(1)}%`} color={COLORS.primary} />
            {project.min_purchase && <StatItem label={t('minPurchase')} value={`$${project.min_purchase}`} />}
            {project.max_purchase && <StatItem label={t('maxPurchase')} value={`$${project.max_purchase}`} />}
          </div>

          {/* Progress Bar */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{
              height: '8px',
              backgroundColor: COLORS.background,
              borderRadius: '4px',
              overflow: 'hidden',
            }}>
              <div style={{
                height: '100%',
                width: `${Math.min(progress, 100)}%`,
                backgroundColor: COLORS.primary,
              }} />
            </div>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              marginTop: '6px',
              fontSize: '11px',
              color: COLORS.textSecondary,
            }}>
              <span>{t('sold')}: {(project.sold_amount || 0).toLocaleString()}</span>
              <span>{t('total')}: {project.total_supply.toLocaleString()}</span>
            </div>
          </div>

          {/* Holding Info */}
          {holding && (
            <div style={{
              padding: '12px',
              backgroundColor: `${COLORS.success}10`,
              border: `1px solid ${COLORS.success}30`,
              borderRadius: '10px',
              marginBottom: '16px',
            }}>
              <div style={{ color: COLORS.textSecondary, fontSize: '11px', marginBottom: '4px' }}>
                {t('yourBalance')}
              </div>
              <div style={{ color: COLORS.success, fontSize: '18px', fontWeight: '700' }}>
                {holding.quantity.toLocaleString()} {t('tokens')}
              </div>
              <div style={{ color: COLORS.textSecondary, fontSize: '12px' }}>
                ≈ ${(holding.quantity * (project.sell_price || project.buy_price)).toFixed(2)}
              </div>
            </div>
          )}

          {/* Buttons */}
          <div style={{ display: 'flex', gap: '10px' }}>
            {project.buy_enabled && (
              <button
                onClick={onBuy}
                style={{
                  flex: 1,
                  padding: '14px',
                  backgroundColor: COLORS.primary,
                  border: 'none',
                  borderRadius: '10px',
                  color: '#000',
                  fontSize: '14px',
                  fontWeight: '700',
                  cursor: 'pointer',
                }}
              >
                {t('buyButton')}
              </button>
            )}
            {holding && project.sell_enabled && project.sell_price && (
              <button
                onClick={() => onSell(holding)}
                style={{
                  flex: 1,
                  padding: '14px',
                  backgroundColor: `${COLORS.danger}15`,
                  border: `1px solid ${COLORS.danger}`,
                  borderRadius: '10px',
                  color: COLORS.danger,
                  fontSize: '14px',
                  fontWeight: '700',
                  cursor: 'pointer',
                }}
              >
                {t('sellButton')}
              </button>
            )}
          </div>
        </div>
      </div>
    </ModalWrapper>
  );
};

// ================== STAT ITEM ==================
const StatItem = ({ label, value, color }) => (
  <div style={{
    padding: '10px',
    backgroundColor: COLORS.background,
    borderRadius: '8px',
  }}>
    <div style={{ color: COLORS.textSecondary, fontSize: '10px', marginBottom: '2px' }}>{label}</div>
    <div style={{ color: color || COLORS.text, fontSize: '14px', fontWeight: '600' }}>{value}</div>
  </div>
);

// ================== MODAL WRAPPER ==================
const ModalWrapper = ({ children, onClose }) => {
  const isMobile = window.innerWidth < 768;
  
  return (
    <div 
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.7)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: isMobile ? 'flex-end' : 'center',
        zIndex: 1000,
        padding: isMobile ? 0 : '20px',
      }}
      onClick={onClose}
    >
      <div onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
};

export default IDOPage;
